import React from "react";

interface PrincipleCardProps {
  number: string;
  title: string;
  description: string;
  items: string[];
}

const PrincipleCard: React.FC<PrincipleCardProps> = ({
  number,
  title,
  description,
  items,
}) => {
  return (
    <div className="bg-white rounded-[10px] shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-[gray]/10">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <span className="text-blue-600 font-bold text-lg">{number}</span>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 leading-relaxed">{description}</p>
        </div>
      </div>

      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-3">
            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2" />
            <span className="text-gray-700 leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ResponsibleAICommitment: React.FC = () => {
  const principles = [
    {
      number: "1",
      title: "Fairness and Bias Mitigation",
      description:
        "We are committed to building AI systems that treat all candidates fairly, regardless of their race, gender, age, ethnicity, or background.",
      items: [
        "Bias Testing: We rigorously test our models to identify and mitigate potential sources of bias. We use diverse datasets for training and regularly audit our system's performance across different demographic groups.",
        "Objective Criteria: Our AI is designed to score candidates based on skills, experience, and role-specific competencies derived from the job description, not on protected characteristics.",
      ],
    },
    {
      number: "2",
      title: "Transparency and Explainability",
      description:
        "We believe both our customers and their candidates deserve to understand how our AI works.",
      items: [
        "For Customers: Our platform provides insights and explanations for its recommendations, allowing recruiters to understand why a candidate was scored a certain way. This empowers recruiters to make the final, informed decision.",
        "For Candidates: We provide clear notice to candidates when they are interacting with an AI-powered system (e.g., in our email templates).",
      ],
    },
    {
      number: "3",
      title: "Human-in-the-Loop",
      description:
        "Our AI is a tool to assist, not replace, human recruiters and hiring managers.",
      items: [
        "AI as a Co-pilot: The AI Recruiter acts as a co-pilot, automating high-volume, repetitive tasks like initial screening and scheduling, to free up human recruiters for more high-value, strategic work like candidate engagement and final decision-making.",
        "Customer Control: Our customers (the recruiters) are always in control. They set the criteria, review the AI's recommendations, and make all final hiring decisions.",
      ],
    },
    {
      number: "4",
      title: "Privacy and Security",
      description:
        "We handle all data, especially sensitive candidate data, with the utmost care and in full compliance with data protection laws like the NDPA and GDPR.",
      items: [
        "Data Minimization: We only collect and process data that is necessary for the recruiting function.",
        "Robust Security: We implement industry-standard technical and organizational measures to protect data.",
        "Data Governance: Our data processing practices are governed by our strict Privacy Policy and Data Processing Addendum (DPA).",
      ],
    },
    {
      number: "5",
      title: "Accountability and Governance",
      description: "We take responsibility for our technology and its impact.",
      items: [
        "Internal Review: We have an internal ethics and review board that oversees the development and deployment of our AI models.",
        "Feedback Loops: We actively seek feedback from our customers and candidates to continuously improve our system and its fairness.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-white-50 py-6 px-3 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-5">
          <h1 className="text-[20px] md:text-[35px] font-[500] mb-3">
            Our Commitment to Responsible AI
          </h1>

          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-600 leading-relaxed mb-5">
              At 25th & Staffing, our mission is to make hiring faster, smarter,
              and fairer. We believe that Artificial Intelligence, when
              developed and deployed responsibly, is a powerful tool to help
              organizations find the best talent while reducing human bias.
            </p>

            <div className="bg-blue-50 text-primary rounded-[10px] p-4 shadow-lg">
              <p className="text-lg font-medium">
                This document outlines our core principles for the ethical and
                responsible development of our AI Recruiter Application.
              </p>
            </div>
          </div>
        </div>

        {/* Principles Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          {principles.map((principle) => (
            <PrincipleCard
              key={principle.number}
              number={principle.number}
              title={principle.title}
              description={principle.description}
              items={principle.items}
            />
          ))}
        </div>

        {/* Ongoing Commitment Section */}
        <div className="bg-white rounded-[10px] shadow-xl p-8 max-w-4xl mx-auto border border-[gray]/10">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Our Commitment to Responsible AI is Ongoing
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We will continue to invest in research, partner with experts, and
              engage with our community to ensure our technology builds a more
              equitable and efficient future for recruitment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponsibleAICommitment;
